import { Badge, Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@staff-app/ui';
import {
  INTL_ALL_STAGE_LABELS,
  INTL_DEVICE_LABELS,
  INTL_ROUTE_LABELS,
  routesUsed,
  type IntlAllRequest,
} from '@staff-app/shared-types';
import { DATA_TABLE_CELLS, DATA_TABLE_HEADER, DATA_TABLE_ROW } from '@/lib/data-table';
import { countryName } from '@/features/intl-requests/hooks/useIntlRequests';
import { IntlInvoiceStatusBadge } from '@/features/intl-requests/components/IntlInvoiceStatusBadge';

/** `paul@optisigns.com` → `paul`. Any other domain is kept, so an outside address still reads as one. */
export function filerName(email?: string | null): string {
  const v = (email ?? '').trim();
  return v.replace(/@optisigns\.com$/i, '') || '—';
}

/**
 * The AIR queue — read-only; a row click opens the panel where every action lives.
 *
 * Tracking reads "not shipped" for an order with no tracking yet, rather than a dash, so "which
 * orders are waiting on us" is answerable by scanning (the RMA queue's wording, for the same reason).
 */
export function IntlAllRequestTable({
  requests,
  onSelect,
}: {
  requests: IntlAllRequest[];
  onSelect: (r: IntlAllRequest) => void;
}) {
  return (
    <Table className={DATA_TABLE_CELLS} data-testid="air-table">
      <TableHeader className={DATA_TABLE_HEADER}>
        <TableRow>
          <TableHead className="whitespace-nowrap">{'Request no.'}</TableHead>
          <TableHead className="whitespace-nowrap">{'Customer'}</TableHead>
          <TableHead className="whitespace-nowrap">{'Ship to'}</TableHead>
          <TableHead className="whitespace-nowrap">{'Item'}</TableHead>
          <TableHead className="text-right whitespace-nowrap">{'Qty'}</TableHead>
          <TableHead className="whitespace-nowrap">{'Routes'}</TableHead>
          <TableHead className="whitespace-nowrap">{'Stage'}</TableHead>
          <TableHead className="whitespace-nowrap">{'Invoice status'}</TableHead>
          <TableHead className="whitespace-nowrap">{'Tracking'}</TableHead>
          <TableHead className="whitespace-nowrap">{'Filed by'}</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {requests.length === 0 ? (
          <TableRow>
            <TableCell colSpan={10} className="text-center text-muted-foreground" data-testid="air-table-empty">
              {'No requests'}
            </TableCell>
          </TableRow>
        ) : null}
        {requests.map((r) => {
          const routes = routesUsed(r.allocations);
          return (
            <TableRow
              key={r.id}
              className={`${DATA_TABLE_ROW} cursor-pointer`}
              onClick={() => onSelect(r)}
              data-testid={`air-row-${r.id}`}
            >
              <TableCell className="font-mono">{r.requestNo}</TableCell>
              <TableCell>
                <div>{r.company || r.contactName || '—'}</div>
                <div className="text-xs text-muted-foreground">{r.contactEmail}</div>
              </TableCell>
              <TableCell>{countryName(r.shipToCountry)}</TableCell>
              {/* One device per line, its quantity on the same line in the next column (Paul,
                  2026-10-28) — easier to scan than "1 × OptiStick, 1 × Pro Player, …". */}
              <TableCell data-testid={`air-row-items-${r.id}`}>
                <ul className="space-y-0.5">
                  {r.lines.map((l) => (
                    <li key={l.deviceModel} className="whitespace-nowrap">
                      {INTL_DEVICE_LABELS[l.deviceModel] ?? l.deviceModel}
                    </li>
                  ))}
                </ul>
              </TableCell>
              <TableCell className="text-right tabular-nums" data-testid={`air-row-qty-${r.id}`}>
                <ul className="space-y-0.5">
                  {r.lines.map((l) => (
                    <li key={l.deviceModel}>{l.quantity}</li>
                  ))}
                </ul>
              </TableCell>
              <TableCell data-testid={`air-row-routes-${r.id}`}>
                {/* One route per line (Paul, 2026-10-28), like Item / Qty beside it. */}
                {routes.length ? (
                  <ul className="space-y-0.5">
                    {routes.map((x) => (
                      <li key={x} className="whitespace-nowrap">
                        {INTL_ROUTE_LABELS[x]}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <span className="text-muted-foreground">{'Not routed'}</span>
                )}
              </TableCell>
              <TableCell>
                <Badge variant="secondary">{INTL_ALL_STAGE_LABELS[r.stage] ?? r.stage}</Badge>
              </TableCell>
              {/* Stripe's word beside our stage — the same badge International Requests uses. */}
              <TableCell className="whitespace-nowrap" data-testid={`air-row-invoice-status-${r.id}`}>
                <IntlInvoiceStatusBadge status={r.invoiceStatus} />
              </TableCell>
              <TableCell data-testid={`air-row-tracking-${r.id}`}>
                {r.shipments.filter((s) => s.ordered).length === 0 ? (
                  <span className="text-muted-foreground">{'—'}</span>
                ) : (
                  <ul className="space-y-0.5">
                    {r.shipments
                      .filter((s) => s.ordered)
                      .map((s) => (
                        <li key={s.route} className="text-xs">
                          <span className="text-muted-foreground">{`${s.routeLabel}: `}</span>
                          {s.trackingNumber ? (
                            <span className="font-mono">{s.trackingNumber}</span>
                          ) : (
                            <span className="text-warning">{'not shipped'}</span>
                          )}
                        </li>
                      ))}
                  </ul>
                )}
              </TableCell>
              {/* The filer's name only — the @optisigns.com domain is the same on every row. */}
              <TableCell className="whitespace-nowrap" data-testid={`air-row-filed-${r.id}`}>
                {filerName(r.submittedBy)}
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}
